import axios from 'axios';
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { FaUserTie, FaEnvelope, FaCalendarAlt, FaCheckCircle } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

import 'react-toastify/dist/ReactToastify.css';

const InvestorTracker = ({ user }) => {
    const [investors, setInvestors] = useState([]);
    const navigate = useNavigate();

    // Fetch investor application data
    useEffect(() => {
        const fetchInvestors = async () => {
            try {
                const userId = user?.id;

                if(!userId) {
                    console.error('User ID not found in localStorage.');
                    return;
                }

                const res = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/api/investor/${userId}/submission`,
                    {
                        withCredentials: true,
                    }
                );
                const data = res.data;
                if(Array.isArray(data)) {
                    setInvestors(data);
                } else if(data && typeof data === 'object') {
                    setInvestors([data]);
                } else {
                    setInvestors([]);
                }
            } catch(err) {
                console.error('Error fetching investors', err);
            }
        };
        fetchInvestors();
    }, [user]);

    // Helper function to get status color
    const getStatusColor = (status) => {
        switch(status?.toLowerCase()) {
            case 'approved':
                return 'bg-green-100 text-green-700';
            case 'pending':
                return 'bg-yellow-100 text-yellow-700';
            case 'rejected':
                return 'bg-red-100 text-red-700';
            default:
                return 'bg-gray-100 text-gray-700';
        }
    };

    // Helper function to get KYC status color
    const getKycStatusColor = (status) => {
        switch(status) {
            case 'APPROVED':
                return 'text-green-600';
            case 'FAILED':
                return 'text-red-500';
            case 'PENDING_ID_SCAN':
                return 'text-amber-500';
            default:
                return 'text-gray-500';
        }
    };

    // Helper function to get status border color
    const getStatusBorderColor = (status) => {
        switch(status?.toLowerCase()) {
            case 'approved':
                return 'border-green-500';
            case 'pending':
                return 'border-yellow-500';
            case 'rejected':
                return 'border-red-500';
            default:
                return 'border-gray-300';
        }
    };

    // Just use all investors since pagination is removed
    const filteredInvestors = investors;

    return (
        <div className='flex flex-col' style={{ padding: '32px' }}>
            <div className='mb-6'>
                <div className='border-b border-gray-200 mb-4'>
                    <div className='flex items-center pb-2'>
                        <FaUserTie className='text-navy mr-2 text-xl' />
                        <h1 className='text-2xl font-bold text-navy'>INVESTORS TRACKER</h1>
                    </div>
                </div>
            </div>

            {filteredInvestors.length === 0 ? (
                <div className='bg-white rounded-lg shadow-sm p-10 text-center border border-gray-100 my-8'>
                    <div className='text-5xl text-gray-300 mb-5'>
                        <FaUserTie className='inline-block' />
                    </div>
                    <p className='text-2xl text-gray-500 mb-3'>No Investors Found</p>
                    <p className='text-gray-400 mb-8 max-w-md mx-auto'>
                        No investor submissions available at this time.
                    </p>
                </div>
            ) : (
                <div className='flex flex-col space-y-6 mb-4'>
                    {filteredInvestors.map((investor) => (
                        <div
                            key={investor.id}
                            className={`bg-white rounded-lg shadow-sm overflow-hidden border-l-4 ${getStatusBorderColor(
                                investor.status
                            )} hover:shadow-lg hover:border-l-[6px] transition-all duration-200 relative group cursor-pointer`}
                            onClick={() => {
                                navigate('/investor-review', {
                                    state: { email: investor.email },
                                });
                            }}
                        >
                            <div className='absolute inset-0 bg-gray-400 bg-opacity-0 group-hover:bg-opacity-5 transition-all duration-200 pointer-events-none'></div>
                            <div className='absolute top-0 left-0 right-0 bg-gray-100 py-1.5 text-center text-xs text-gray-600 font-medium border-b border-gray-100 opacity-0 group-hover:opacity-100 transition-opacity duration-200'>
                                Click on the card to view investor details
                            </div>
                            <div className='p-6 pt-8'>
                                <div className='flex justify-between items-center mb-5'>
                                    <h3
                                        className='font-semibold text-xl text-navy'
                                        title={`${investor.first_name} ${investor.last_name}`}
                                    >
                                        {investor.first_name} {investor.last_name}
                                    </h3>
                                    <span
                                        className={`text-xs font-bold px-3 py-1 rounded-full ${getStatusColor(
                                            investor.status
                                        )}`}
                                    >
                                        {investor.status}
                                    </span>
                                </div>

                                <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-4 gap-x-8 mb-6'>
                                    <div className='flex items-center'>
                                        <div className='mr-3'>
                                            <FaEnvelope className='text-gray-400 text-lg' />
                                        </div>
                                        <div>
                                            <p className='text-xs text-gray-500 font-medium'>
                                                EMAIL
                                            </p>
                                            <p className='text-sm text-gray-700'>
                                                {investor.email}
                                            </p>
                                        </div>
                                    </div>

                                    {investor.submitted_at && (
                                        <div className='flex items-center'>
                                            <div className='mr-3'>
                                                <FaCalendarAlt className='text-gray-400 text-lg' />
                                            </div>
                                            <div>
                                                <p className='text-xs text-gray-500 font-medium'>
                                                    SUBMITTED
                                                </p>
                                                <p className='text-sm text-gray-700'>
                                                    {new Date(
                                                        investor.submitted_at
                                                    ).toLocaleDateString()}
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    <div className='flex items-center'>
                                        <div className='mr-3'>
                                            <FaCheckCircle className='text-gray-400 text-lg' />
                                        </div>
                                        <div>
                                            <p className='text-xs text-gray-500 font-medium'>
                                                KYC STATUS
                                            </p>
                                            <p
                                                className={`text-sm font-semibold ${getKycStatusColor(
                                                    investor.north_capital_kyc_status
                                                )}`}
                                            >
                                                {investor.north_capital_kyc_status || 'NOT STARTED'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

InvestorTracker.propTypes = {
    user: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
};

export default InvestorTracker;
